import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { toasterState } from "Components/common/toaster";
import { AWAIT, BLOCK } from "../agent/agentContract";

// Turns that are still a question to the user: actions on them are noise.
const WAITING = new Set([
  AWAIT.QUICK_REPLY,
  AWAIT.CTA,
  AWAIT.CONFIRMATION,
  AWAIT.QUESTION_FORM,
  AWAIT.SELLER_PROFILE_PREVIEW,
  AWAIT.WEBSITE_INPUT
]);

const relative = ts => {
  const diff = Math.max(0, Date.now() - ts);
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d} days ago`;
};

const proseOf = message =>
  (message.blocks || [])
    .filter(b => b.type === BLOCK.TEXT && (b.markdown || "").trim())
    .map(b => b.markdown.trim())
    .join("\n\n");

/**
 * Hover row under a finished assistant turn: copy and time. Only things
 * that do something: no thumbs, and retry appears only on a failed turn,
 * where it stays visible without hover.
 */
const MessageActions = observer(({ message, prevUserText, isLast }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  if (message.streaming) return null;
  if (isLast && copilotStore.streaming) return null;
  if (isLast && WAITING.has(copilotStore.awaiting)) return null;

  const failed = String(message.id).startsWith("err_");
  const prose = proseOf(message);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prose);
      toasterState.setToastMsg("Copied", "success");
    } catch (e) {
      toasterState.setToastMsg("Couldn't copy", "failure");
    }
  };
  const retry = () => copilotStore.sendUserMessage(prevUserText);

  return (
    <div className={`copilotMsgActions${failed ? " is-visible" : ""}`}>
      {prose && !failed && (
        <button type="button" className="copilotMsgActions__btn" title="Copy" onClick={copy}>
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              fill="currentColor"
              d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
            />
          </svg>
        </button>
      )}
      {failed && prevUserText && (
        <button
          type="button"
          className="copilotMsgActions__btn"
          title="Retry"
          disabled={copilotStore.streaming}
          onClick={retry}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              fill="currentColor"
              d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
            />
          </svg>
          {failed && <span>Retry</span>}
        </button>
      )}
      {message.ts > 0 && (
        <span className="copilotMsgActions__time" title={new Date(message.ts).toLocaleString()}>
          {relative(message.ts)}
        </span>
      )}
    </div>
  );
});

export default MessageActions;
